import React, { Suspense, useEffect, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Start fetching early, before the section is on screen.
const LOAD_ROOT_MARGIN_DESKTOP = "0px 0px 75% 0px";
// Mobile: avoid mounting many heavy sections during a fling scroll.
const LOAD_ROOT_MARGIN_MOBILE = "0px 0px 20% 0px";
// Fade in once the section actually enters the viewport.
const REVEAL_ROOT_MARGIN = "0px 0px -10% 0px";
const REVEAL_THRESHOLD = 0.06;

const REVEAL_DURATION_MS = 1400;
const REVEAL_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
const REVEAL_OFFSET_Y = 10;

function RevealContent({ children, revealed, prefersReducedMotion }) {
  const transition = prefersReducedMotion
    ? "none"
    : `opacity ${REVEAL_DURATION_MS}ms ${REVEAL_EASING}, transform ${REVEAL_DURATION_MS}ms ${REVEAL_EASING}`;

  return (
    <Box
      sx={{
        opacity: revealed ? 1 : 0,
        transform: revealed ? "translateY(0)" : `translateY(${REVEAL_OFFSET_Y}px)`,
        transition,
        willChange: revealed ? "auto" : "opacity, transform",
      }}
    >
      {children}
    </Box>
  );
}

export default function RevealSection({
  id,
  minHeight,
  mode = "reveal",
  loadRootMargin,
  children,
}) {
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [shouldReveal, setShouldReveal] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const effectiveLoadRootMargin =
    loadRootMargin ||
    (isMobile ? LOAD_ROOT_MARGIN_MOBILE : LOAD_ROOT_MARGIN_DESKTOP);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const loadObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          loadObserver.unobserve(element);
        }
      },
      { root: null, rootMargin: effectiveLoadRootMargin, threshold: 0 },
    );

    loadObserver.observe(element);
    return () => loadObserver.disconnect();
  }, [effectiveLoadRootMargin]);

  useEffect(() => {
    if (!shouldLoad || mode === "load-only") return undefined;

    const element = containerRef.current;
    if (!element) return undefined;

    if (prefersReducedMotion) {
      setShouldReveal(true);
      return undefined;
    }

    const revealObserver = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= REVEAL_THRESHOLD
        ) {
          setShouldReveal(true);
          revealObserver.unobserve(element);
        }
      },
      {
        root: null,
        rootMargin: REVEAL_ROOT_MARGIN,
        threshold: [0, REVEAL_THRESHOLD, 0.12, 0.2],
      },
    );

    revealObserver.observe(element);
    return () => revealObserver.disconnect();
  }, [shouldLoad, prefersReducedMotion, mode]);

  const revealed =
    mode === "load-only" ? shouldLoad : shouldLoad && (prefersReducedMotion || shouldReveal);

  const loadedContent =
    mode === "load-only" ? (
      children
    ) : (
      <RevealContent revealed={revealed} prefersReducedMotion={prefersReducedMotion}>
        {children}
      </RevealContent>
    );

  return (
    <Box
      id={id}
      ref={containerRef}
      sx={{
        minHeight: minHeight || "auto",
        position: "relative",
        zIndex: 1,
        bgcolor: "#fff",
        isolation: "isolate",
      }}
    >
      {shouldLoad && <Suspense fallback={null}>{loadedContent}</Suspense>}
    </Box>
  );
}
